import { Edit, CalendarToday, AccessTime } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Stack,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import { FC, useMemo } from "react";
import { Link } from "react-router-dom";
import { FeedbackStatus, FeedbackType } from "types/feedback";
import { User } from "types/user";
import { isFeedbackExpired, shouldHideFeedback } from "utils/feedback";

const FeedbackDateInfo: FC<{ feedback: FeedbackType }> = ({ feedback }) => (
  <Stack direction="row" spacing={2} mt={0.5}>
    {feedback.observation_date && (
      <Stack direction="row" spacing={0.5} alignItems="center">
        <CalendarToday sx={{ fontSize: 13, color: "text.secondary" }} />
        <Typography variant="caption" color="text.secondary">
          {dayjs(feedback.observation_date).format("MMM D, YYYY")}
        </Typography>
      </Stack>
    )}
    {feedback.created_at && (
      <Stack direction="row" spacing={0.5} alignItems="center">
        <AccessTime sx={{ fontSize: 13, color: "text.secondary" }} />
        <Typography variant="caption" color="text.secondary">
          {dayjs(feedback.created_at).format("h:mm A")}
        </Typography>
      </Stack>
    )}
  </Stack>
);

const UpcomingTaskItemStudent: FC<{ feedback: FeedbackType }> = ({
  feedback,
}) => (
  <Box display="flex" alignItems="center" key={feedback.feedback_id}>
    <Box py={1} px={2}>
      <Edit color="primary" />
    </Box>
    <Box flexGrow={1} py={1} px={2} fontSize="1rem">
      <Typography>Pending Feedback sign off from coach {feedback.coach?.name}.</Typography>
      <FeedbackDateInfo feedback={feedback} />
    </Box>
    <Box py={1} px={2}>
      <Button
        to={`/feedback/${feedback.feedback_id}`}
        size="small"
        component={Link}
      >
        View
      </Button>
    </Box>
  </Box>
);

const UpcomingTaskItemCoach: FC<{ feedback: FeedbackType }> = ({
  feedback,
}) => (
  <Box display="flex" alignItems="center" key={feedback.feedback_id}>
    <Box py={1} px={2}>
      <Edit color="primary" />
    </Box>
    <Box flexGrow={1} py={1} px={2} fontSize="1rem">
      <Typography>Pending Feedback from {feedback.student?.name}</Typography>
      <FeedbackDateInfo feedback={feedback} />
    </Box>
    <Box py={1} px={2}>
      <Button
        to={`/feedback/${feedback.feedback_id}`}
        size="small"
        component={Link}
      >
        View
      </Button>
    </Box>
  </Box>
);

type UpcomingTasksProps = {
  feedbacks: FeedbackType[];
  user: User;
};

export const UpcomingTasks: FC<UpcomingTasksProps> = ({ feedbacks, user }) => {
  const feedbackList = useMemo(() => {
    return feedbacks.filter(
      (feedback) =>
        feedback.status === FeedbackStatus.PENDING &&
        !isFeedbackExpired(feedback) &&
        !shouldHideFeedback(feedback),
    );
  }, [feedbacks]);

  return (
    <Card>
      <CardContent>
        <Typography variant="h5" gutterBottom>
          Upcoming Tasks
        </Typography>

        <Stack spacing={2}>
          {feedbackList.length === 0 && (
            <Box py={1}>
              <Alert severity="info">No upcoming tasks</Alert>
            </Box>
          )}
          {feedbackList.map((feedback) =>
            user.role === "student" ? (
              <UpcomingTaskItemStudent
                feedback={feedback}
                key={feedback.feedback_id}
              />
            ) : (
              <UpcomingTaskItemCoach
                feedback={feedback}
                key={feedback.feedback_id}
              />
            ),
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};
